import {
  RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import { ApiJobId } from 'api/types/ApiTypedId';
import { useInViewportEffect } from 'hooks/viewport/useInViewportEffect';
import { useViewJobPageSidebarResultsScroll } from 'modules/listing/pages/jobs/view/components/Sidebar/useViewJobPageSidebarResultsScroll';
import { SearchAlgoliaHit } from 'modules/search/algolia/types/SearchAlgoliaHit';
import { SearchHit } from 'modules/search/components/Hit/SearchHit';
import { SearchLoadingSectionMockResults } from 'modules/search/components/Loading/SearchLoadingSectionMockResults';
import { SearchContextHeaderData } from 'modules/search/containers/SearchContextHeader/SearchContextHeaderData';
import { fetchSearchPageResults } from 'modules/search/zustand-stores/searchStore';
import { algoliaTrackClick } from 'utils/algolia/algoliaTrackClick';
import { trackClicked } from 'utils/analytics/track/trackClicked';
import { trackEvent } from 'utils/analytics/track/trackEvent';

const hitsPerPage = 20;

// We actually only need one to show that we're loading, but add some in case the user clicked on a result that's at the bottom of the page
// that will allow us to scroll it to the start of the scrolling parent while we load the next page results
const numberOfMocksResultsToShow = 3;

type Props = {
  selectedJobId: ApiJobId;
  selectedJobPage: number;
  selectedJobPageHits: SearchAlgoliaHit[];
  queryID: string | undefined;
  totalPages: number;
  searchContext: SearchContextHeaderData;
  scrollingParentRef: RefObject<HTMLElement | null>;
};

export function ViewJobPageSidebarPaginationHits({
  selectedJobId,
  selectedJobPage: page,
  queryID,
  selectedJobPageHits,
  totalPages,
  searchContext,
  scrollingParentRef,
}: Props) {
  const [combinedResults, setCombinedResults] = useState([
    { page, hits: selectedJobPageHits },
  ]);

  const [scrollLoadingIndexes, setScrollLoadingData] = useState({
    first: page,
    last: page,
  });

  // Previous
  const oldScrollHeightRef = useRef<number>();

  const hasPreviousPage = scrollLoadingIndexes.first > 0;
  const onLoadPreviousPage = useCallback(async () => {
    // This function was called again because the scroll has not been adjusted yet
    if (oldScrollHeightRef.current) return;

    const newFirstPage = scrollLoadingIndexes.first - 1;
    const previousPageResult = await fetchSearchPageResults(newFirstPage);
    trackEvent('Loaded: Previous Listings');

    oldScrollHeightRef.current = scrollingParentRef.current?.scrollHeight;

    setCombinedResults((current) => [
      { page: newFirstPage, hits: previousPageResult.hits },
      ...current,
    ]);

    setScrollLoadingData((current) => ({
      first: newFirstPage,
      last: current.last,
    }));
  }, [scrollLoadingIndexes.first, scrollingParentRef]);

  useLayoutEffect(() => {
    const scrollingParent = scrollingParentRef.current;

    const oldScrollHeight = oldScrollHeightRef.current;
    if (!scrollingParent || !oldScrollHeight) return;

    const newScrollHeight = scrollingParent.scrollHeight;
    const delta = newScrollHeight - oldScrollHeight;
    const newScrollPosition = scrollingParent.scrollTop + delta;

    scrollingParent.scrollTop = newScrollPosition;
    oldScrollHeightRef.current = undefined;
  }, [combinedResults, scrollingParentRef]);

  const loadPreviousElRef = useRef<HTMLDivElement>(null);
  useInViewportEffect({
    ref: loadPreviousElRef,
    callback: useCallback(
      (inViewportStatus) => {
        if (inViewportStatus.inViewport) onLoadPreviousPage();
      },
      [onLoadPreviousPage],
    ),
  });

  // Next
  const hasNextPage = scrollLoadingIndexes.last < totalPages - 1;
  const onLoadNextPage = useCallback(async () => {
    const newLastPage = scrollLoadingIndexes.last + 1;
    const nextPageResult = await fetchSearchPageResults(newLastPage);
    trackEvent('Loaded: More Listings');

    setCombinedResults((current) => [
      ...current,
      { page: newLastPage, hits: nextPageResult.hits },
    ]);

    setScrollLoadingData((current) => ({
      first: current.first,
      last: newLastPage,
    }));
  }, [scrollLoadingIndexes.last]);

  const loadNextElRef = useRef<HTMLDivElement>(null);
  useInViewportEffect({
    ref: loadNextElRef,
    callback: useCallback(
      (inViewportStatus) => {
        if (inViewportStatus.inViewport) onLoadNextPage();
      },
      [onLoadNextPage],
    ),
  });

  const { scrollToJobId } = useViewJobPageSidebarResultsScroll({
    scrollingParentRef,
  });

  const firstJobIdRef = useRef(selectedJobId);
  useEffect(() => {
    // Scroll when the user lands on the page
    scrollToJobId(firstJobIdRef.current);
  }, [scrollToJobId]);

  const selectedJobIdViaHitClickRef = useRef<ApiJobId>();
  useEffect(() => {
    if (selectedJobIdViaHitClickRef.current === selectedJobId) return;

    selectedJobIdViaHitClickRef.current = undefined;

    // Scroll when user clicks on `previous` or `next` on the search context header
    scrollToJobId(selectedJobId);
  }, [scrollToJobId, selectedJobId]);

  return (
    <>
      {hasPreviousPage && (
        <div ref={loadPreviousElRef}>
          <SearchLoadingSectionMockResults items={numberOfMocksResultsToShow} />
        </div>
      )}

      {combinedResults.map((item) =>
        item.hits.map((hit, index) => (
          <SearchHit
            key={hit.objectID}
            hit={hit}
            hideOwnerBanner
            highlighted={selectedJobId === hit.objectID}
            trackClick={() => {
              selectedJobIdViaHitClickRef.current = hit.objectID;
              searchContext.setPageIndex(item.page, false);

              algoliaTrackClick(
                queryID,
                hit.objectID,
                // Position in algolia analytics is 1-indexed
                item.page * hitsPerPage + index + 1,
              );
              trackClicked('Search Result', {
                org_id: 'orgID' in hit ? hit.orgID : undefined,
                org_type: 'orgType' in hit ? hit.orgType : undefined,
                result_id: hit.objectID,
                result_index: index,
                result_type: hit.type,
              });
            }}
          />
        )),
      )}

      {hasNextPage && (
        <div
          ref={loadNextElRef}
          style={{ paddingTop: 600, marginTop: -600, zIndex: -1 }}
        >
          <SearchLoadingSectionMockResults items={numberOfMocksResultsToShow} />
        </div>
      )}
    </>
  );
}
